import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';

export function Sheet({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title?: string; children: ComponentChildren; footer?: ComponentChildren }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <div class="fixed inset-0 z-50 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <div class="absolute inset-0 bg-ink-900/40 backdrop-blur-[2px]" onClick={onClose} />
      <div class="relative w-full sm:max-w-xl max-h-[92dvh] flex flex-col bg-cream-50 rounded-t-3xl sm:rounded-3xl shadow-2xl">
        <div class="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 class="font-serif text-xl font-bold text-ink-900 truncate pr-4">{title}</h2>
          <button class="h-9 w-9 rounded-full bg-cream-200 text-ink-700 text-lg leading-none" onClick={onClose} aria-label="Închide">×</button>
        </div>
        <div class="overflow-y-auto px-5 pb-4 flex-1">{children}</div>
        {footer && <div class="border-t border-cream-300 px-5 py-3 safe-bottom flex gap-2 justify-end">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ComponentChildren }) {
  return <label class="block"><span class="label">{label}</span>{children}</label>;
}

export function Avatar({ member, size = 28 }: { member?: { name: string; avatar?: string | null; color?: string | null } | null; size?: number }) {
  const av = member?.avatar || '';
  const style = { width: size, height: size, background: member?.color || '#B8532F', fontSize: size * 0.55 };
  if (/^(https?:|\/img\/|data:)/.test(av)) return <img src={av} alt={member?.name} class="rounded-full object-cover" style={style} />;
  return (
    <span class="inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0" style={style} title={member?.name}>
      {av || (member?.name || '?').charAt(0).toUpperCase()}
    </span>
  );
}

export function Stars({ value, onChange, size = 'text-2xl' }: { value: number; onChange?: (v: number) => void; size?: string }) {
  return (
    <div class="flex gap-0.5" role={onChange ? 'radiogroup' : undefined} aria-label="Evaluare">
      {[1, 2, 3, 4, 5].map(i => (
        <button type="button" disabled={!onChange} aria-label={`${i} stele`}
          class={`${size} leading-none ${i <= value ? 'text-amber-500' : 'text-cream-300'} ${onChange ? 'cursor-pointer' : 'cursor-default'}`}
          onClick={() => onChange?.(i === value ? 0 : i)}>★</button>
      ))}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ComponentChildren }) {
  return (
    <div class="py-16 text-center text-ink-500">
      <div class="text-4xl mb-3">📚</div>
      <div class="font-serif text-lg text-ink-700">{title}</div>
      {children && <div class="mt-2 text-sm">{children}</div>}
    </div>
  );
}
